import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {AusgangsrechnungInfo} from "../../api";
import DataTable from "../data/DataTable";
import {formatAsMoney} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";

export type AusgangsrechnungenFilter = {
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<AusgangsrechnungenFilter>, "filter"> &
    Partial<Pick<DataRequest<AusgangsrechnungenFilter>, "filter">>;

const tableName: string = "ausgangsrechnungen"

export default function AusgangsrechnungenDataTable({...input}: Props) {
    const request = useDataRequest<AusgangsrechnungenFilter>({
        filter: {},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <AusgangsrechnungenResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<AusgangsrechnungenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AusgangsrechnungenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-ausgangsrechnung");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-ausgangsrechnung");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function AusgangsrechnungenResults({
                                       request,
                                       setFilteredState,
                                   }: {
    request: DataRequestState<AusgangsrechnungenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<AusgangsrechnungenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAusgangsrechnungen: DataLoader<
        AusgangsrechnungenFilter,
        AusgangsrechnungInfo
    > = useCallback(
        async (params) =>
            (await getApi()).ausgangsrechnungen.sucheAusgangsrechnungen({
                ...params,
            }),
        [getApi]
    );

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAusgangsrechnungen}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open(`/ausgangsrechnungen/${row.id}`, "_blank");
                } else {
                    navigate(`/ausgangsrechnungen/${row.id}`);
                }
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<AusgangsrechnungInfo>> {
    const {t: translation} = useT("ausgangsrechnung");

    return useMemo(
        () => [
            {
                field: "faelligkeitsDatum",
                headerName: capitalize(translation("date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) =>
                    value
                        ? new Date(value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : "kein Datum",
            },
            {
                field: "rechnungsNummer",
                headerName: capitalize(translation("invoice-number")),
                flex: 1,
            },
            {
                field: "art",
                headerName: capitalize(translation("type")),
            },
            {
                field: "kundenName",
                headerName: capitalize(translation("customer-name")),
                flex: 1,
            },
            {
                field: "baustellenNummer",
                headerName: capitalize(translation("construction-site-number")),
                flex: 1,
                align: "right",
                headerAlign: "right",
            },
            {
                field: "baustellenName",
                headerName: capitalize(translation("construction-site-name")),
                flex: 1,
            },
            {
                field: "lfsNr",
                headerName: capitalize(translation("lfs-No")),
                flex: 1,
            },
            {
                field: "bemerkung",
                headerName: capitalize(translation("remark")),
                flex: 1,
            },
            {
                field: "buchungsart",
                headerName: capitalize(translation("booking-type")),
                flex: 1,
            },
            {
                field: "gesamtPreis",
                headerName: capitalize(translation("total-price")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
        ],
        [translation]
    );
}
